/**
 * ClockDial -- SVG circular time picker with draggable start/end handles.
 * 24-hour clock face with exterior arc showing shift duration.
 * Handles snap to 15-minute increments.
 * Displays total shift duration prominently in the center.
 */

import { useRef, useCallback, useEffect, useState } from 'react';

// -- Constants --
const SIZE = 300;
const CX = SIZE / 2;
const CY = SIZE / 2;
const CLOCK_R = 105; // clock face radius (slightly smaller to make room for exterior arc)
const HANDLE_R = 16; // handle circle radius (touch target)
const TICK_OUTER = CLOCK_R - 4;
const TICK_INNER_MAJOR = CLOCK_R - 14;
const TICK_INNER_MINOR = CLOCK_R - 9;
const NUMBER_R = CLOCK_R - 28;
const ARC_R = CLOCK_R + 16; // arc drawn OUTSIDE the clock face
const ARC_WIDTH = 10; // width of the exterior arc stroke

// Colors
const ON_COLOR = 'oklch(0.72 0.19 165)';
const OFF_COLOR = 'oklch(0.55 0.20 25)';
const ARC_COLOR = 'oklch(0.72 0.19 165)';
const ARC_LAP_COLOR = 'oklch(0.72 0.19 165 / 0.3)';

// -- Utility functions --

/** Convert minutes-since-midnight (0–1439) to angle on 24hr clock (0° = 0:00/top, clockwise). */
function minutesToAngle(minutes: number): number {
  const m24 = ((minutes % 1440) + 1440) % 1440;
  return (m24 / 1440) * 360;
}

/** Convert angle to minutes-since-midnight, snapped to 15min. */
function angleToMinutes(angleDeg: number): number {
  const normalized = ((angleDeg % 360) + 360) % 360;
  const rawMinutes = (normalized / 360) * 1440;
  const snapped = Math.round(rawMinutes / 15) * 15;
  return snapped % 1440;
}

/** Angle (degrees) from center to point, 0° = top (0:00), clockwise. */
function getAngle(cx: number, cy: number, px: number, py: number): number {
  const dx = px - cx;
  const dy = py - cy;
  const angle = Math.atan2(dx, -dy) * (180 / Math.PI);
  return ((angle % 360) + 360) % 360;
}

/** Convert angle + radius to x,y. */
function polarToXY(angleDeg: number, r: number): { x: number; y: number } {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

/** Build SVG arc path from startAngle to endAngle (clockwise) at given radius. */
function arcPath(startDeg: number, endDeg: number, r: number): string {
  const s = ((startDeg % 360) + 360) % 360;
  let e = ((endDeg % 360) + 360) % 360;
  if (e <= s) e += 360;
  const sweep = e - s;
  if (sweep < 0.5) return ''; // avoid degenerate arcs
  const largeArc = sweep > 180 ? 1 : 0;

  const startRad = ((s - 90) * Math.PI) / 180;
  const endRad = ((s + sweep - 90) * Math.PI) / 180;

  const x1 = CX + r * Math.cos(startRad);
  const y1 = CY + r * Math.sin(startRad);
  const x2 = CX + r * Math.cos(endRad);
  const y2 = CY + r * Math.sin(endRad);

  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

/** Format minutes-since-midnight as "18:00" (24h). */
function formatTime24h(minutes: number): string {
  const h24 = Math.floor((((minutes % 1440) + 1440) % 1440) / 60);
  const m = ((minutes % 60) + 60) % 60;
  return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Format duration in minutes to human-readable string. */
function formatDuration(totalMinutes: number): string {
  if (totalMinutes < 0) totalMinutes = 0;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// -- Props --

export interface ClockDialProps {
  /** Start time as minutes since midnight (0–1439). */
  startMinutes: number;
  /** End time as minutes since midnight (0–1439). */
  endMinutes: number;
  /** Duration in minutes (may exceed 24h for multi-day shifts). */
  durationMinutes: number;
  onStartChange: (minutes: number) => void;
  onEndChange: (minutes: number) => void;
  disabled?: boolean;
  /** Optional date label shown under start time (e.g. "Mar 11") */
  startDateLabel?: string;
  /** Optional date label shown under end time (e.g. "Mar 12") */
  endDateLabel?: string;
  /** Date value for start (YYYY-MM-DD) — makes the date label a clickable picker */
  startDateValue?: string;
  /** Date value for end (YYYY-MM-DD) — makes the date label a clickable picker */
  endDateValue?: string;
  /** Called when user picks a new start date via the inline date picker */
  onStartDateChange?: (date: string) => void;
  /** Called when user picks a new end date via the inline date picker */
  onEndDateChange?: (date: string) => void;
}

// -- Component --

export default function ClockDial({
  startMinutes,
  endMinutes,
  durationMinutes,
  onStartChange,
  onEndChange,
  disabled = false,
  startDateLabel,
  endDateLabel,
  startDateValue,
  endDateValue,
  onStartDateChange,
  onEndDateChange,
}: ClockDialProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const draggingRef = useRef<'start' | 'end' | null>(null);
  const [activeHandle, setActiveHandle] = useState<'start' | 'end' | null>(null);

  const startAngle = minutesToAngle(startMinutes);
  const endAngle = minutesToAngle(endMinutes);

  // Convert screen coords to SVG coords
  const screenToSVG = useCallback((clientX: number, clientY: number): { x: number; y: number } => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    const pt = new DOMPoint(clientX, clientY);
    const svgPt = pt.matrixTransform(inv);
    return { x: svgPt.x, y: svgPt.y };
  }, []);

  const handlePointerDown = useCallback(
    (handle: 'start' | 'end', e: React.PointerEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      draggingRef.current = handle;
      setActiveHandle(handle);
      (e.target as Element).setPointerCapture(e.pointerId);
    },
    [disabled],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const handle = draggingRef.current;
      if (!handle) return;
      e.preventDefault();

      const svgPt = screenToSVG(e.clientX, e.clientY);
      const newAngle = getAngle(CX, CY, svgPt.x, svgPt.y);
      const newMinutes = angleToMinutes(newAngle);

      if (handle === 'start') {
        onStartChange(newMinutes);
      } else {
        onEndChange(newMinutes);
      }
    },
    [screenToSVG, onStartChange, onEndChange],
  );

  const handlePointerUp = useCallback(() => {
    draggingRef.current = null;
    setActiveHandle(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      draggingRef.current = null;
    };
  }, []);

  // -- Derived values --

  // Determine how many full laps the duration covers
  const fullLaps = Math.floor(durationMinutes / 1440);
  const samePosition = Math.abs(startAngle - endAngle) < 1 || Math.abs(startAngle - endAngle) > 359;

  // Handle positions — place on exterior arc
  const startPos = polarToXY(startAngle, ARC_R);
  const endPos = polarToXY(endAngle, ARC_R);

  // 24-hour numbers: 0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22
  const hourLabels = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];

  // -- Render --

  return (
    <div className="w-full flex flex-col items-center">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="w-full max-w-[280px] select-none"
        style={{ touchAction: 'none' }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        role="group"
        aria-label="Shift time clock dial"
      >
        {/* Clock face */}
        <circle cx={CX} cy={CY} r={CLOCK_R} className="fill-surface-sunken stroke-edge-default" strokeWidth={2} />

        {/* Exterior arc track (background ring) */}
        <circle
          cx={CX}
          cy={CY}
          r={ARC_R}
          fill="none"
          className="stroke-edge-muted"
          strokeWidth={ARC_WIDTH}
          opacity={0.3}
        />

        {/* Full-lap underlay ring(s) — show completed 24h laps */}
        {fullLaps > 0 && (
          <circle cx={CX} cy={CY} r={ARC_R} fill="none" stroke={ARC_LAP_COLOR} strokeWidth={ARC_WIDTH} />
        )}
        {fullLaps > 1 && (
          <text
            x={CX}
            y={CY + 38}
            textAnchor="middle"
            dominantBaseline="central"
            className="fill-content-tertiary text-[10px] font-medium select-none"
            style={{ pointerEvents: 'none' }}
          >
            {fullLaps}x around
          </text>
        )}

        {/* Highlighted exterior arc from start to end */}
        {!samePosition && (
          <path
            d={arcPath(startAngle, endAngle, ARC_R)}
            fill="none"
            stroke={ARC_COLOR}
            strokeWidth={ARC_WIDTH}
            strokeLinecap="round"
            opacity={0.85}
          />
        )}

        {/* When ON and OFF overlap: show a half-green/half-orange split circle */}
        {samePosition && durationMinutes > 0 && (
          <>
            {/* Full lap arc shown above already covers the ring */}
            <circle cx={CX} cy={CY} r={ARC_R} fill="none" stroke={ARC_COLOR} strokeWidth={ARC_WIDTH} opacity={0.85} />
          </>
        )}

        {/* Tick marks — 48 ticks for 24 hours (one per 30 min) */}
        {Array.from({ length: 48 }, (_, i) => {
          const angle = (i / 48) * 360;
          const isMajor = i % 4 === 0; // every 2 hours
          const isMinor2 = i % 2 === 0; // every hour
          const outer = polarToXY(angle, TICK_OUTER);
          const inner = polarToXY(angle, isMajor ? TICK_INNER_MAJOR : isMinor2 ? TICK_INNER_MINOR : TICK_INNER_MINOR);
          return (
            <line
              key={`tick-${i}`}
              x1={outer.x}
              y1={outer.y}
              x2={inner.x}
              y2={inner.y}
              className={isMajor ? 'stroke-content-secondary' : 'stroke-content-tertiary'}
              strokeWidth={isMajor ? 2 : 1}
              strokeLinecap="round"
            />
          );
        })}

        {/* Hour numbers (every 2 hours on 24h face) */}
        {hourLabels.map((h, i) => {
          const angle = (i / 12) * 360;
          const pos = polarToXY(angle, NUMBER_R);
          return (
            <text
              key={`hour-${h}`}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-content-secondary text-[11px] font-medium select-none"
              style={{ pointerEvents: 'none' }}
            >
              {h}
            </text>
          );
        })}

        {/* Center display: large duration */}
        <text
          x={CX}
          y={CY - 6}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-content-primary text-[28px] font-bold select-none"
          style={{ pointerEvents: 'none' }}
        >
          {formatDuration(durationMinutes)}
        </text>
        <text
          x={CX}
          y={CY + 18}
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-content-tertiary text-[11px] select-none"
          style={{ pointerEvents: 'none' }}
        >
          total
        </text>

        {/* Start (IN) handle — on the exterior arc */}
        {samePosition && durationMinutes > 0 ? (
          <>
            {/* Split handle: half green (ON) / half orange (OFF) */}
            <defs>
              <clipPath id="clip-left-half">
                <rect x={startPos.x - HANDLE_R} y={startPos.y - HANDLE_R} width={HANDLE_R} height={HANDLE_R * 2} />
              </clipPath>
              <clipPath id="clip-right-half">
                <rect x={startPos.x} y={startPos.y - HANDLE_R} width={HANDLE_R} height={HANDLE_R * 2} />
              </clipPath>
            </defs>
            {/* ON half (left) */}
            <circle
              cx={startPos.x}
              cy={startPos.y}
              r={HANDLE_R}
              fill={ON_COLOR}
              fillOpacity={disabled ? 0.4 : 0.9}
              stroke={ON_COLOR}
              strokeWidth={activeHandle === 'start' ? 3 : 2}
              clipPath="url(#clip-left-half)"
              className={`cursor-grab ${activeHandle === 'start' ? 'cursor-grabbing' : ''}`}
              onPointerDown={(e) => handlePointerDown('start', e)}
              role="slider"
              aria-label="On duty time"
              aria-valuemin={0}
              aria-valuemax={1439}
              aria-valuenow={startMinutes}
              aria-valuetext={formatTime24h(startMinutes)}
              tabIndex={disabled ? -1 : 0}
            />
            {/* OFF half (right) */}
            <circle
              cx={startPos.x}
              cy={startPos.y}
              r={HANDLE_R}
              fill={OFF_COLOR}
              fillOpacity={disabled ? 0.4 : 0.9}
              stroke={OFF_COLOR}
              strokeWidth={activeHandle === 'end' ? 3 : 2}
              clipPath="url(#clip-right-half)"
              className={`cursor-grab ${activeHandle === 'end' ? 'cursor-grabbing' : ''}`}
              onPointerDown={(e) => handlePointerDown('end', e)}
              role="slider"
              aria-label="Off duty time"
              aria-valuemin={0}
              aria-valuemax={1439}
              aria-valuenow={endMinutes}
              aria-valuetext={formatTime24h(endMinutes)}
              tabIndex={disabled ? -1 : 0}
            />
            {/* Dividing line */}
            <line
              x1={startPos.x}
              y1={startPos.y - HANDLE_R}
              x2={startPos.x}
              y2={startPos.y + HANDLE_R}
              stroke="white"
              strokeWidth={1.5}
              style={{ pointerEvents: 'none' }}
            />
            {/* Labels */}
            <text
              x={startPos.x - HANDLE_R / 2}
              y={startPos.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-white text-[7px] font-bold select-none"
              style={{ pointerEvents: 'none' }}
            >
              ON
            </text>
            <text
              x={startPos.x + HANDLE_R / 2}
              y={startPos.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-white text-[7px] font-bold select-none"
              style={{ pointerEvents: 'none' }}
            >
              OFF
            </text>
          </>
        ) : (
          <>
            {/* Separate ON and OFF handles */}
            <circle
              cx={startPos.x}
              cy={startPos.y}
              r={HANDLE_R}
              className={`cursor-grab ${activeHandle === 'start' ? 'cursor-grabbing' : ''}`}
              fill={ON_COLOR}
              fillOpacity={disabled ? 0.4 : 0.9}
              stroke={ON_COLOR}
              strokeWidth={activeHandle === 'start' ? 3 : 2}
              onPointerDown={(e) => handlePointerDown('start', e)}
              role="slider"
              aria-label="On duty time"
              aria-valuemin={0}
              aria-valuemax={1439}
              aria-valuenow={startMinutes}
              aria-valuetext={formatTime24h(startMinutes)}
              tabIndex={disabled ? -1 : 0}
            />
            <text
              x={startPos.x}
              y={startPos.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-white text-[9px] font-bold select-none"
              style={{ pointerEvents: 'none' }}
            >
              ON
            </text>

            <circle
              cx={endPos.x}
              cy={endPos.y}
              r={HANDLE_R}
              className={`cursor-grab ${activeHandle === 'end' ? 'cursor-grabbing' : ''}`}
              fill={OFF_COLOR}
              fillOpacity={disabled ? 0.4 : 0.9}
              stroke={OFF_COLOR}
              strokeWidth={activeHandle === 'end' ? 3 : 2}
              onPointerDown={(e) => handlePointerDown('end', e)}
              role="slider"
              aria-label="Off duty time"
              aria-valuemin={0}
              aria-valuemax={1439}
              aria-valuenow={endMinutes}
              aria-valuetext={formatTime24h(endMinutes)}
              tabIndex={disabled ? -1 : 0}
            />
            <text
              x={endPos.x}
              y={endPos.y}
              textAnchor="middle"
              dominantBaseline="central"
              className="fill-white text-[9px] font-bold select-none"
              style={{ pointerEvents: 'none' }}
            >
              OFF
            </text>
          </>
        )}
      </svg>

      {/* Digital time display below the clock */}
      <div className="flex items-center gap-4 mt-2">
        <div className="text-center">
          <div className="text-[13px] uppercase tracking-wider text-content-tertiary">On Duty</div>
          <div className="text-lg font-mono font-semibold text-content-primary" style={{ color: ON_COLOR }}>
            {formatTime24h(startMinutes)}
          </div>
          {startDateLabel && onStartDateChange && startDateValue ? (
            <label className="relative cursor-pointer group">
              <div className="text-[13px] text-content-tertiary mt-0.5 group-hover:text-action-primary transition-colors underline decoration-dotted underline-offset-2">
                {startDateLabel}
              </div>
              <input
                type="date"
                value={startDateValue}
                onChange={(e) => onStartDateChange(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                tabIndex={-1}
              />
            </label>
          ) : (
            startDateLabel && <div className="text-[13px] text-content-tertiary mt-0.5">{startDateLabel}</div>
          )}
        </div>
        <div className="text-content-tertiary text-xl">&rarr;</div>
        <div className="text-center">
          <div className="text-[13px] uppercase tracking-wider text-content-tertiary">Off Duty</div>
          <div className="text-lg font-mono font-semibold text-content-primary" style={{ color: OFF_COLOR }}>
            {formatTime24h(endMinutes)}
          </div>
          {endDateLabel && onEndDateChange && endDateValue ? (
            <label className="relative cursor-pointer group">
              <div className="text-[13px] text-content-tertiary mt-0.5 group-hover:text-action-primary transition-colors underline decoration-dotted underline-offset-2">
                {endDateLabel}
              </div>
              <input
                type="date"
                value={endDateValue}
                onChange={(e) => onEndDateChange(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                tabIndex={-1}
              />
            </label>
          ) : (
            endDateLabel && <div className="text-[13px] text-content-tertiary mt-0.5">{endDateLabel}</div>
          )}
        </div>
      </div>
    </div>
  );
}
